import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton, Badge, Popover, Box, Typography, List, ListItem, ListItemText,
  Divider, Button, Chip, CircularProgress,
} from '@mui/material';
import {
  Notifications as BellIcon,
  NotificationsNone as BellOutlineIcon,
  PersonAdd as AssignedIcon,
  EventAvailable as QueueIcon,
  PlayCircleOutline as TurnIcon,
  Description as NoteIcon,
  EventNote as ApptIcon,
  TimerOutlined as ExpiringIcon,
  DeleteOutline as ExpiredIcon,
  AccountCircle as DefaultIcon,
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationsContext';

const TYPE_META = {
  patient_assigned: { icon: <AssignedIcon fontSize="small" />, label: 'Assigned' },
  queue_added: { icon: <QueueIcon fontSize="small" />, label: 'Queue' },
  your_turn: { icon: <TurnIcon fontSize="small" />, label: 'Your turn' },
  note_ready: { icon: <NoteIcon fontSize="small" />, label: 'Note' },
  appointment_requested: { icon: <ApptIcon fontSize="small" />, label: 'Request' },
  protocol_question_assigned: { icon: <NoteIcon fontSize="small" />, label: 'Protocol' },
  recording_expiring: { icon: <ExpiringIcon fontSize="small" />, label: 'Expiring' },
  recording_expired: { icon: <ExpiredIcon fontSize="small" />, label: 'Deleted' },
  report_added: { icon: <NoteIcon fontSize="small" />, label: 'Report' },
};

// Where a notification opens. Rows created before notifications carried a `link`
// fall back to the page their type implies.
const LEGACY_ROUTES = {
  queue_added: '/queue',
  patient_assigned: '/queue',
  your_turn: '/queue',
};

const routeFor = (n) => n.link || LEGACY_ROUTES[n.type] || null;

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { items, unread, refresh, markAllRead, markRead } = useNotifications();
  const [anchor, setAnchor] = useState(null);
  const [loading, setLoading] = useState(false);

  const open = (e) => {
    setAnchor(e.currentTarget);
    setLoading(true);
    refresh().finally(() => setLoading(false));
  };
  const close = () => setAnchor(null);

  const handleItemClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    const to = routeFor(n);
    if (to) {
      close();
      navigate(to);
    }
  };

  return (
    <>
      <IconButton onClick={open} size="small" sx={{ color: 'text.secondary' }}>
        <Badge badgeContent={unread} color="error" overlap="circular">
          {unread > 0 ? <BellIcon /> : <BellOutlineIcon />}
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxWidth: '90vw' } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
          <Button size="small" onClick={markAllRead} disabled={unread === 0}>
            Mark all read
          </Button>
        </Box>
        <Divider />
        {loading && items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No notifications yet</Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 480, overflow: 'auto', py: 0 }}>
            {items.map((n) => {
              const meta = TYPE_META[n.type] || {
                icon: <DefaultIcon fontSize="small" />,
                // Never show the raw enum name: "recording_expiring" is a database
                // value, not something to put in front of a clinician.
                label: String(n.type || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
              };
              return (
                <ListItem
                  key={n.id}
                  button
                  onClick={() => handleItemClick(n)}
                  sx={{
                    bgcolor: n.is_read ? 'transparent' : 'action.hover',
                    alignItems: 'flex-start',
                    borderLeft: n.is_read ? '3px solid transparent' : '3px solid',
                    borderLeftColor: n.is_read ? 'transparent' : 'primary.main',
                    cursor: routeFor(n) ? 'pointer' : 'default',
                  }}
                >
                  <Box sx={{ mr: 1.5, mt: 0.5, color: 'primary.main' }}>{meta.icon}</Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={n.is_read ? 500 : 700}>
                          {n.title}
                        </Typography>
                        <Chip size="small" label={meta.label} variant="outlined" />
                      </Box>
                    }
                    secondary={
                      <>
                        {n.message && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {n.message}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {relativeTime(n.created_at)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button size="small" fullWidth onClick={() => { close(); navigate('/notifications'); }}>
            See all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
}
